import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useModules } from '../contexts/ModuleContext'
import PageErrorBoundary from './PageErrorBoundary'
import CommandPalette from './global/CommandPalette'
import SyncStatusBar from './SyncStatusBar'
import NetworkToast from './NetworkToast'
import InstallPrompt from './InstallPrompt'
import NotificationsPrompt from './NotificationsPrompt'
import TelegramLinkPrompt from './TelegramLinkPrompt'
import AIChatWidget from './layout/AIChatWidget'
import AssistantsMenu from './layout/AssistantsMenu'
import NotificationBell from './layout/NotificationBell'
import ChatPanel from './chat/ChatPanel'
// L&D Super-UX Phase 3 PR 3.2 — Layout-level AI Coach Pane
import AiCoachPane from './ai-coach/AiCoachPane'
import '../styles/conversation.css'
import { apiFetch } from '../api/client'
import { apiImpersonationTargets } from '../api/auth'
import SupportWidget from './Support/SupportWidget'
import AutoErrorToast from './Support/AutoErrorToast'
import DepartingEmployeeBanner from './DepartingEmployeeBanner'
import StartCenterActionBanner from './StartCenterActionBanner'
import {
    Kanban, Users, Briefcase, FileCheck, Building2, ExternalLink,
    Search, Clock, User, Wallet, Settings, X,
    Inbox, LogOut, Wifi, WifiOff,
    CalendarClock,
    Menu,
    CalendarCheck, Palette, Eye,
    ChevronDown, Sun, Moon,
} from 'lucide-react'
import { applyTheme } from '../utils/theme'
import { displayName } from '../utils/nameHelpers'
import '../styles/dashboard-theme.css'
import '../styles/exchange-dashboard.css'
import '../styles/manager-dashboard.css'
import './layout/workspace-shell.css'
import WorkspaceNavigation from './layout/WorkspaceNavigation.jsx'
import { useIsMobileLayout } from '../hooks/useMediaQuery'
import { TYPE_LABELS, PAGE_TITLES, ROLES, NAV_PERSONAL, ALL_NAV_REGISTRY } from './layout/navConfig'
import { lsGet, lsSet, MAX_RECENT, LS_PINNED, LS_RECENT } from './layout/NavSections'
import { useMyTrackNav } from '../hooks/useMyTrackNav'
import SectionTabs from './layout/SectionTabs'
import SettingsMenu from './layout/SettingsMenu'
import ScopeDiagnosticsModal from './layout/ScopeDiagnosticsModal'
import { LayoutWorkspaceContext, useLayoutWorkspaceMode } from '../contexts/LayoutWorkspaceContext'

// ── Search database — dynamically populated from API cache ──
const SEARCH_DB = [] // No hardcoded search entries — API-first

export default function Layout() {
    const auth = useAuth()
    const [searchQuery, setSearchQuery] = useState('')
    const [showSearchResults, setShowSearchResults] = useState(false)
    const isAdmin = Boolean(auth.hasPermission?.('all') || auth.user?.is_platform_admin)
    const [viewAsRole] = useState(null) // reserved: role-preview state (kept for effectiveUser plumbing)
    // Аккаунт-меню под аватаркой (правый верхний угол топбара) — личность + настройки.
    const [showProfileMenu, setShowProfileMenu] = useState(false)
    // «Войти как» — реальная имперсонизация. admin → представитель на роль (mode=roles);
    // руководитель → его команда/поддерево (mode=people); рядовой сотрудник → пусто.
    const [impData, setImpData] = useState({ mode: null, targets: [], loaded: false })
    useEffect(() => {
        if (!showProfileMenu || auth.isImpersonating || impData.loaded) return
        apiImpersonationTargets()
            .then((d) => setImpData({ mode: d?.mode || 'people', targets: d?.targets || [], loaded: true }))
            .catch(() => setImpData({ mode: null, targets: [], loaded: true }))
    }, [showProfileMenu, auth.isImpersonating, impData.loaded])
    // Тема-переключатель (light/dark) как быстрый circle-icon в топбаре.
    const [theme, setTheme] = useState(() => localStorage.getItem('onboardix_theme') || 'light')
    const toggleTheme = useCallback(() => {
        setTheme(prev => {
            const next = prev === 'dark' ? 'light' : 'dark'
            applyTheme(next)
            return next
        })
    }, [])
    const [scopeDiag, setScopeDiag] = useState(null) // Impersonate-banner scope diagnostics
    const closeScopeDiagnostics = useCallback(() => setScopeDiag(null), [])
    const [mobileNavOpen, setMobileNavOpen] = useState(false)
    const { workspaceMode, workspaceContextValue } = useLayoutWorkspaceMode(setMobileNavOpen)
    const [showCmdPalette, setShowCmdPalette] = useState(false)
    const openCommandPalette = useCallback(() => {
        setMobileNavOpen(false)
        setShowProfileMenu(false)
        setShowSearchResults(false)
        setShowCmdPalette(true)
    }, [])
    // L&D Super-UX Phase 3 PR 3.2 — layout-level AI Coach Pane (доступен с любой страницы)
    const [showAiCoachPane, setShowAiCoachPane] = useState(false)
    // Messenger unread count — broadcastится из ChatPanel через
    // `onboardix:messenger:unread`, используется в AssistantsMenu (бейдж).
    const [messengerUnread, setMessengerUnread] = useState(0)
    // «Мой адаптационный трек» в «Личном кабинете» показывается только тому, у
    // кого есть активные записи на трек. auth.user — не effectiveUser: тот
    // объявлен ниже и в этой точке ещё в TDZ (уронил бы весь Layout).
    const personalNav = useMyTrackNav(auth.user?.employee_id, NAV_PERSONAL)
    // Pinned nav items (Set of route strings, localStorage-backed)
    const [pinnedRoutes, setPinnedRoutes] = useState(() => new Set(lsGet(LS_PINNED, [])))
    // Recent nav items (array of route strings newest-first, localStorage-backed)
    const [recentRoutes, setRecentRoutes] = useState(() => lsGet(LS_RECENT, []))
    // Stable callback for nav-item taps — closes drawer regardless of whether
    // the route actually changed (re-tapping the current page keeps drawer open
    // otherwise, since location.pathname-effect won't fire).
    const closeMobileNav = useCallback(() => setMobileNavOpen(false), [])
    const mobileNavTriggerRef = useRef(null)
    const isMobileLayout = useIsMobileLayout()
    useEffect(() => { if (!isMobileLayout) setMobileNavOpen(false) }, [isMobileLayout])
    const location = useLocation()
    const navigate = useNavigate()
    const searchRef = useRef(null)
    const searchInputRef = useRef(null)
    const { isModuleVisible } = useModules()
    const profileRef = useRef(null)

    // Get current page title.
    // PAGE_TITLES содержит и одно-сегментные ('candidates'), и двух-сегментные
    // ('admin/cold-start', 'workflow/runs', 'me/learning', 'admin/watchers',
    // 'admin/ai-drafts') ключи. Сначала пробуем составной ключ из двух
    // сегментов, потом первый сегмент. На root ('/' или '/erp/') → «Дашборд».
    // Иначе — derive из последнего сегмента path (capitalize, dashes→spaces),
    // чтобы не показывать вводящий в заблуждение «Дашборд» на чужих страницах.
    const pathParts = location.pathname.split('/').filter(Boolean)
    const segments = pathParts[0] === 'erp' ? pathParts.slice(1) : pathParts
    const seg1 = segments[0] || ''
    const seg2 = segments[1] || ''
    const twoSegKey = seg1 && seg2 ? `${seg1}/${seg2}` : ''
    const lastSeg = segments[segments.length - 1] || ''
    const looksLikeUuidOrId = /^[0-9a-f-]{8,}$|^\d+$/i.test(lastSeg)
    // Для UUID-«хвоста» (/admin/dsar/abc-123-uuid) — берём предпоследний сегмент.
    const labelSeg = (looksLikeUuidOrId && segments.length >= 2 ? segments[segments.length - 2] : lastSeg) || ''
    const derivedTitle = labelSeg
        ? labelSeg.replace(/-/g, ' ').replace(/^./, c => c.toUpperCase())
        : 'Onboardix'
    const rootFallback = seg1 === '' ? PAGE_TITLES[''] : derivedTitle
    // ВАЖНО: twoSegKey может быть '' (когда seg2 пустой) — тогда PAGE_TITLES['']
    // возвращает 'Дашборд' и короткое замыкание `||` уходит до PAGE_TITLES[seg1]
    // не доходит. Guard `twoSegKey &&` отсекает этот путь и пропускает дальше.
    // Аналогично seg1 может быть '' на root '/' → пропускаем к rootFallback.
    const pageTitle = (twoSegKey && PAGE_TITLES[twoSegKey]) || (seg1 && PAGE_TITLES[seg1]) || rootFallback

    // Close panels on outside click
    useEffect(() => {
        function handler(e) {
            if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchResults(false)
            if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfileMenu(false)
        }
        function onEscape(e) {
            if (e.key !== 'Escape' || e.defaultPrevented) return
            if (searchRef.current?.contains(document.activeElement)) searchInputRef.current?.focus()
            setShowSearchResults(false)
            setShowProfileMenu(false)
            if (profileRef.current?.contains(document.activeElement)) profileRef.current.querySelector('button')?.focus()
        }
        document.addEventListener('mousedown', handler)
        document.addEventListener('keydown', onEscape)
        return () => {
            document.removeEventListener('mousedown', handler)
            document.removeEventListener('keydown', onEscape)
        }
    }, [])

    // Подписка на unread-broadcast из ChatPanel для бейджа на AssistantsMenu.
    useEffect(() => {
        if (typeof window === 'undefined') return undefined
        const onUnread = (e) => {
            const count = Number(e?.detail?.count) || 0
            setMessengerUnread(count)
        }
        window.addEventListener('onboardix:messenger:unread', onUnread)
        return () => window.removeEventListener('onboardix:messenger:unread', onUnread)
    }, [])

    // Ctrl+K / Cmd+K — открывает CommandPalette (Phase 6 PR-H).
    // Двойной shortcut: Cmd+K (Mac) или Ctrl+K (Windows/Linux).
    useEffect(() => {
        function handler(e) {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault()
                openCommandPalette()
            }
        }
        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [openCommandPalette])

    // Close search on navigation; track recent pages
    useEffect(() => {
        setShowSearchResults(false)
        setSearchQuery('')
        setMobileNavOpen(false)
        // Трекаем последние посещённые разделы для блока «Недавние»
        const pathParts2 = location.pathname.split('/').filter(Boolean)
        const segs = pathParts2[0] === 'erp' ? pathParts2.slice(1) : pathParts2
        const seg1 = segs[0] || ''
        const seg2 = segs[1] || ''
        const routeKey = (seg1 && seg2 && !(/^\d+$|^[0-9a-f-]{8,}$/i.test(seg2)))
            ? `${seg1}/${seg2}`
            : seg1
        if (routeKey && ALL_NAV_REGISTRY.some(i => i.to === routeKey)) {
            setRecentRoutes(prev => {
                const next = [routeKey, ...prev.filter(r => r !== routeKey)].slice(0, MAX_RECENT)
                lsSet(LS_RECENT, next)
                return next
            })
        }
    }, [location.pathname])

    // Toggle pin для пункта меню (QW-3)
    const togglePin = useCallback((routeTo) => {
        setPinnedRoutes(prev => {
            const next = new Set(prev)
            if (next.has(routeTo)) next.delete(routeTo)
            else next.add(routeTo)
            lsSet(LS_PINNED, [...next])
            return next
        })
    }, [])


    // Build dynamic search index from cached API data
    const dynamicSearchDB = (() => {
        const results = []
        try {
            // Employees from cache
            const empCache = localStorage.getItem('onboardix_cache_dashboard_employees')
            if (empCache) {
                const empRaw = JSON.parse(empCache)
                const empArr = Array.isArray(empRaw) ? empRaw : (empRaw?.items || [])
                empArr.filter(e => e.status !== 'terminated').forEach(e => {
                    const name = displayName(e.full_name) || `${e.last_name || ''} ${e.first_name || ''}`.trim()
                    if (name) results.push({
                        type: 'employee', Icon: User, color: 'var(--color-info)',
                        title: name, subtitle: `${e.department_name || e.department || '—'} · ${e.status === 'active' ? 'Активен' : e.status || ''}`,
                        path: '/erp/employees',
                    })
                })
            }
            // Vacancies from cache
            const vacCache = localStorage.getItem('onboardix_cache_vacancies')
            if (vacCache) {
                const vacRaw = JSON.parse(vacCache)
                const vacArr = Array.isArray(vacRaw) ? vacRaw : (vacRaw?.items || [])
                vacArr.forEach(v => {
                    if (v.title) results.push({
                        type: 'vacancy', Icon: Briefcase, color: 'var(--color-success)',
                        title: v.title, subtitle: `${v.company || ''} · ${v.location || ''}`.trim(),
                        path: '/erp/vacancies',
                    })
                })
            }
            results.push({
                type: 'page', Icon: Wallet, color: 'var(--color-accent)',
                title: 'Корпоративные расходы',
                subtitle: 'Заявки, конверты, очередь на оплату',
                path: '/erp/expenses',
            })
            results.push({
                type: 'page', Icon: FileCheck, color: 'var(--color-success)',
                title: 'Согласование расходов',
                subtitle: 'Очередь на одобрение по организации',
                path: '/erp/expense-approvals',
            })
        } catch { /* ignore */ }
        return results
    })()

    // Search results — dynamic from API cache
    const searchResults = searchQuery.length >= 2
        ? dynamicSearchDB.filter(r => r.title.toLowerCase().includes(searchQuery.toLowerCase()) || r.subtitle.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 8)
        : []

    const baseRole = viewAsRole || auth.user?.role
    // Admin-defined custom roles (e.g. "Завхоз") have no entry in the static ROLES map.
    // Backend fills `role_meta = {label, icon, color}` in /auth/me for any non-fixed role,
    // so the sidebar renders the real label/color instead of falling back to "Сотрудник".
    const apiRoleMeta = auth.user?.role_meta || {}
    const customRoleLabel = (auth.user?.custom_role_label || '').trim()
    const roleCfg = ROLES[baseRole] || {
        label: apiRoleMeta.label || customRoleLabel || baseRole || ROLES.employee.label,
        Icon: User,
        color: apiRoleMeta.color || 'var(--color-accent)',
        iconEmoji: apiRoleMeta.icon || '',
    }

    const effectiveUser = useMemo(() => {
        if (!isAdmin || !viewAsRole || viewAsRole === auth.user?.role) return auth.user;
        return { role: viewAsRole, extra_modules: [] }; // Preview as role without any extras
    }, [isAdmin, viewAsRole, auth.user]);

    // Видимые разделы для CommandPalette (QW-1)
    const navItemsForPalette = useMemo(() => {
        if (!effectiveUser) return []
        return ALL_NAV_REGISTRY.filter(item =>
            isModuleVisible(item.moduleKey || item.to || 'dashboard', effectiveUser)
        )
    }, [effectiveUser, isModuleVisible])


    return (
        <LayoutWorkspaceContext.Provider value={workspaceContextValue}>
        <div className={`layout layout--signal${workspaceMode ? ' layout--workspace-expanded' : ''}`}>
            <WorkspaceNavigation
                effectiveUser={effectiveUser}
                personalNav={personalNav}
                allItems={navItemsForPalette}
                pinnedRoutes={pinnedRoutes}
                recentRoutes={recentRoutes}
                onTogglePin={togglePin}
                mobileOpen={mobileNavOpen}
                onClose={closeMobileNav}
                triggerRef={mobileNavTriggerRef}
            />
            {/* Main Content */}
            <main className="main-content" inert={isMobileLayout && mobileNavOpen ? true : undefined}>
                {/* Impersonation Banner */}
                {auth.isImpersonating && (
                    <div
                        style={{
                            background: 'var(--color-warning-bg)',
                            color: 'var(--color-warning)',
                            padding: '8px 16px',
                            fontWeight: '600'
                        }}
                        className='text-center text-md flex justify-center items-center gap-12 border-bottom'>
                        <span style={{display:'inline-flex', alignItems:'center', gap:6}}><Eye size={14} /> Вы просматриваете систему от лица сотрудника ({displayName(auth.user?.full_name) || auth.user?.login})</span>
                        <button
                            onClick={async () => {
                                try {
                                    const res = await apiFetch('/auth/me/candidates-scope')
                                    setScopeDiag(res)
                                } catch (e) {
                                    setScopeDiag({ error: e?.message || String(e) })
                                }
                            }}
                            style={{
                                background: 'transparent',
                                color: 'var(--color-warning)',
                                border: '1px solid var(--color-warning)',
                                padding: '4px 12px',
                                borderRadius: '4px',
                                fontWeight: 'bold'
                            }}
                            className='text-xs cursor-pointer'><Search size={14} /> Диагностика scope</button>
                        <button
                            onClick={() => auth.stopImpersonating()}
                            style={{
                                background: 'var(--color-warning)',
                                color: 'var(--color-text-inverse)',
                                border: 'none',
                                padding: '4px 12px',
                                borderRadius: '4px',
                                fontWeight: 'bold'
                            }}
                            className='text-xs cursor-pointer'>Завершить просмотр</button>
                    </div>
                )}

                {/* Scope Diagnostics Modal — открывается из импersonation banner */}
                {scopeDiag && (
                    <ScopeDiagnosticsModal
                        scopeDiag={scopeDiag}
                        onClose={closeScopeDiagnostics}
                        onRefresh={setScopeDiag}
                        user={auth.user}
                    />
                )}
                {/* Top Bar */}
                <header className="topbar">
                    <button
                        type="button"
                        className="layout__burger"
                        ref={mobileNavTriggerRef}
                        aria-controls="workspace-navigation"
                        onClick={() => setMobileNavOpen(v => !v)}
                        aria-label={mobileNavOpen ? 'Закрыть меню' : 'Открыть меню'}
                        aria-expanded={mobileNavOpen}
                    >
                        {mobileNavOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                    <NavLink to="/" className="workspace-brand" aria-label="Onboardix — главная">
                        <img src="/logo-mark-light.svg" alt="" width="32" height="32" />
                        <span>Onboardix</span>
                    </NavLink>
                    <div className="workspace-tenant">
                        <img src="/brand/6-light.svg" alt="Senate Exchange" />
                        <span aria-hidden="true">Senate Exchange</span>
                    </div>
                    <button type="button" className="topbar__icon-btn workspace-search-trigger" aria-label="Поиск по разделам" onClick={openCommandPalette}><Search size={20} /></button>
                    {!location.pathname.includes('/partner') && (
                        <div className="topbar__search relative" ref={searchRef}>
                            <Search size={16} className="topbar__search-icon" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="topbar__search-input"
                                placeholder="Поиск сотрудников, кандидатов, вакансий…"
                                aria-label="Поиск сотрудников, кандидатов и вакансий"
                                value={searchQuery}
                                onChange={(e) => { setSearchQuery(e.target.value); setShowSearchResults(true) }}
                                onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                            />
                            <button type="button" className="topbar__kbd" onClick={openCommandPalette} aria-label="Открыть поиск по разделам, Ctrl+K">Ctrl+K</button>

                        {/* Search results dropdown */}
                        {showSearchResults && searchQuery.length >= 2 && (
                            <div className="layout-dropdown search-dropdown">
                                {searchResults.length === 0 ? (
                                    <div className="search-dropdown__empty">Ничего не найдено</div>
                                ) : (
                                    searchResults.map((r, i) => (
                                        <button type="button" key={i}
                                            onClick={() => { navigate(r.path); setShowSearchResults(false); setSearchQuery('') }}
                                            className="search-dropdown__item"
                                        >
                                            <div className="search-dropdown__icon flex items-center justify-center" style={{ background: `color-mix(in srgb, ${r.color} 8%, transparent)`, color: r.color }}>
                                                {r.Icon && <r.Icon size={16} />}
                                            </div>
                                            <div className="search-dropdown__info">
                                                <div className="search-dropdown__title">{r.type === 'employee' ? displayName(r.title) : r.title}</div>
                                                <div className="search-dropdown__subtitle">{r.subtitle}</div>
                                            </div>
                                            <span className="search-dropdown__type" style={{ background: `color-mix(in srgb, ${r.color} 6%, transparent)`, color: r.color }}>
                                                {TYPE_LABELS[r.type]}
                                            </span>
                                        </button>
                                    ))
                                )}
                            </div>
                        )}
                        </div>
                    )}
                    <div className="topbar__actions relative">
                        {!isMobileLayout && <AssistantsMenu onOpenAiCoach={() => setShowAiCoachPane(true)} messengerUnread={messengerUnread} />}
                        <NotificationBell />
                        {!isMobileLayout && <>
                            <button type="button" className="topbar__icon-btn" onClick={toggleTheme} title={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'} aria-label="Переключить тему">
                                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                            </button>
                            <SettingsMenu effectiveUser={effectiveUser} />
                        </>}

                        {/* Аккаунт-меню под аватаркой — личность + настройки */}
                        <div className="topbar__profile" ref={profileRef}>
                            <button
                                type="button"
                                className="topbar__profile-trigger"
                                onClick={() => setShowProfileMenu(v => !v)}
                                aria-expanded={showProfileMenu}
                                aria-label="Меню профиля"
                            >
                                <span
                                    className="topbar__profile-avatar flex items-center justify-center"
                                >
                                    {auth.user?.avatar_url
                                        ? <img src={auth.user.avatar_url} alt="" className="topbar__profile-avatar-img" />
                                        : (roleCfg.iconEmoji
                                            ? <span style={{ fontSize: 16, lineHeight: 1 }}>{roleCfg.iconEmoji}</span>
                                            : (roleCfg.Icon && <roleCfg.Icon size={18} />))}
                                </span>
                                <span className="topbar__profile-meta">
                                    <span className="topbar__profile-name">{displayName(auth.user?.full_name) || roleCfg.label}</span>
                                    <span className="topbar__profile-role">
                                        {auth.offlineMode ? <><WifiOff size={9} /> Офлайн</> : <><Wifi size={9} /> {roleCfg.label}</>}
                                    </span>
                                </span>
                                <ChevronDown
                                    size={15}
                                    className="topbar__profile-chevron text-muted"
                                    style={{ transform: showProfileMenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                                />
                            </button>

                            {showProfileMenu && (
                                <div className="layout-dropdown profile-dropdown">
                                    {/* Шапка с крупной аватаркой и идентичностью */}
                                    <div className="profile-dropdown__header">
                                        <span
                                            className="profile-dropdown__avatar flex items-center justify-center"
                                                >
                                            {auth.user?.avatar_url
                                                ? <img src={auth.user.avatar_url} alt="" className="topbar__profile-avatar-img" />
                                                : (roleCfg.iconEmoji
                                                    ? <span style={{ fontSize: 22, lineHeight: 1 }}>{roleCfg.iconEmoji}</span>
                                                    : (roleCfg.Icon && <roleCfg.Icon size={24} />))}
                                        </span>
                                        <div className="profile-dropdown__identity">
                                            <div className="profile-dropdown__name">{displayName(auth.user?.full_name) || roleCfg.label}</div>
                                            <div className="profile-dropdown__sub">{roleCfg.label}</div>
                                            {auth.user?.email && <div className="profile-dropdown__email">{auth.user.email}</div>}
                                        </div>
                                    </div>

                                    {isMobileLayout && <div className="workspace-profile-tools">
                                        <AssistantsMenu onOpenAiCoach={() => { setShowProfileMenu(false); setShowAiCoachPane(true) }} messengerUnread={messengerUnread} />
                                        <button type="button" className="topbar__icon-btn" onClick={toggleTheme} aria-label="Переключить тему">{theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}</button>
                                        <SettingsMenu effectiveUser={effectiveUser} />
                                    </div>}
                                    {/* Личность + настройки */}
                                    <div className="profile-dropdown__items">
                                        <button className="profile-dropdown__item" onClick={() => { setShowProfileMenu(false); navigate('/cabinet') }}>
                                            <User size={16} /> <span>Мой профиль</span>
                                        </button>
                                        <button className="profile-dropdown__item" onClick={() => { setShowProfileMenu(false); navigate('/cabinet?tab=security') }}>
                                            <Settings size={16} /> <span>Настройки и безопасность</span>
                                        </button>
                                        <button className="profile-dropdown__item" onClick={() => { setShowProfileMenu(false); navigate('/cabinet?tab=appearance') }}>
                                            <Palette size={16} /> <span>Внешний вид</span>
                                        </button>
                                    </div>

                                    {/* Войти как — реальная имперсонизация. admin: по роли; руководитель: по сотруднику */}
                                    {!auth.isImpersonating && impData.targets.length > 0 && (
                                        <div className="profile-dropdown__roles">
                                            <div className="profile-dropdown__roles-title">
                                                {impData.mode === 'roles' ? 'Войти как роль' : 'Войти как сотрудника'}
                                            </div>
                                            <div className="profile-dropdown__roles-list">
                                                {impData.targets.map((t) => {
                                                    const cfg = ROLES[t.role] || { label: t.role, Icon: Users, color: 'var(--color-accent)' }
                                                    const primary = impData.mode === 'roles' ? cfg.label : displayName(t.full_name)
                                                    return (
                                                        <button key={t.user_id}
                                                            onClick={() => { setShowProfileMenu(false); auth.impersonate(t.user_id) }}
                                                            className="role-menu__item"
                                                            style={{ '--role-color': cfg.color }}
                                                            title={`Войти как ${displayName(t.full_name)} (${cfg.label}) — увидеть его реальный доступ`}
                                                        >
                                                            <span className="role-menu__item-icon" style={{ color: cfg.color }}>
                                                                {cfg.Icon && <cfg.Icon size={16} />}
                                                            </span>
                                                            <span>{primary}</span>
                                                            {impData.mode === 'people' && (
                                                                <span className="role-menu__item-sub" style={{ marginInlineStart: 'auto', opacity: 0.6, fontSize: 12 }}>{cfg.label}</span>
                                                            )}
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Смотреть как — не-админ с привязкой к тест-аккаунту (view_as_bindings) */}
                                    {!isAdmin && !auth.isImpersonating && auth.viewAsTargets?.length > 0 && (
                                        <div className="profile-dropdown__roles">
                                            <div className="profile-dropdown__roles-title">Смотреть как</div>
                                            <div className="profile-dropdown__roles-list">
                                                {auth.viewAsTargets.map(t => (
                                                    <button key={t.id}
                                                        onClick={() => { setShowProfileMenu(false); auth.viewAs(t.id) }}
                                                        className="role-menu__item"
                                                        style={{ '--role-color': 'var(--color-info)' }}
                                                    >
                                                        <span className="role-menu__item-icon" style={{ color: 'var(--color-info)' }}>
                                                            <Eye size={16} />
                                                        </span>
                                                        <span>{displayName(t.full_name) || ROLES[t.role]?.label || t.login}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Выход */}
                                    <div className="profile-dropdown__footer">
                                        <button className="profile-dropdown__item profile-dropdown__item--danger" onClick={auth.logout}>
                                            <LogOut size={16} /> <span>Выйти</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>
                <div className="workspace-page-heading">
                    <h1 className="topbar__title">{pageTitle}</h1>
                </div>

                {/* Banner для уволенного сотрудника — режим передачи дел */}
                <DepartingEmployeeBanner />

                {/* Полоса «Нужно ваше действие» — пока есть незакрытые шаги старта */}
                <StartCenterActionBanner />

                {/* Полоса вкладок сгруппированного раздела (navGroups.js).
                    Рисуется только если текущий маршрут входит в группу. */}
                <SectionTabs effectiveUser={effectiveUser} />

                <PageErrorBoundary>
                    <Outlet />
                </PageErrorBoundary>

                {/* ═══ Network Status Toast — top center ═══ */}
                <NetworkToast />

                {/* ═══ PWA Install Prompt — appears 8s after first nav ═══ */}
                <InstallPrompt />

                {/* ═══ Web Push opt-in — appears 30s after nav, only if backend has VAPID ═══ */}
                <NotificationsPrompt />

                {/* ═══ Telegram link nudge — appears 50s after nav, only if not yet linked ═══ */}
                <TelegramLinkPrompt />

                {/* ═══ Sync Status Bar — floating widget ═══ */}
                <SyncStatusBar />

                {/* ═══ AI Chat Assistant — floating widget ═══ */}
                <AIChatWidget />

                {/* ═══ Messenger Panel — right drawer ═══ */}
                <ChatPanel />

                {/* ═══ AI Coach Pane — открывается из топбарного AssistantsMenu ═══ */}
                <AiCoachPane open={showAiCoachPane} onClose={() => setShowAiCoachPane(false)} />

                {/* ═══ Command Palette (Ctrl+K / Cmd+K) — включает разделы навигации ═══ */}
                <CommandPalette open={showCmdPalette} onClose={() => setShowCmdPalette(false)} navItems={navItemsForPalette} />

                {/* ═══ BB-1: Мобильный bottom-nav для dept_head / head ═══ */}
                {(effectiveUser?.role === 'dept_head' || effectiveUser?.role === 'cmo' || effectiveUser?.role === 'head') && (
                    <nav className="mobile-bottom-nav" aria-label="Быстрая навигация">
                        {[
                            { to: 'dept-dashboard', icon: Building2, label: 'Отдел' },
                            { to: 'myteam', icon: Users, label: 'Команда' },
                            { to: 'team-timesheet', icon: Clock, label: 'Табель' },
                            { to: 'work-schedules', icon: CalendarClock, label: 'Графики' },
                            { to: 'team-payments', icon: Wallet, label: 'Ведомость' },
                            { to: 'inbox', icon: Inbox, label: 'Inbox' },
                        ].map(({ to, icon: Icon, label }) => (
                            <NavLink
                                key={to}
                                to={to}
                                className={({ isActive }) => `mobile-bottom-nav__item ${isActive ? 'active' : ''}`}
                                onClick={closeMobileNav}
                            >
                                <Icon size={20} />
                                <span>{label}</span>
                            </NavLink>
                        ))}
                    </nav>
                )}

            </main>
            <div className="workspace-global-overlays" inert={isMobileLayout && mobileNavOpen ? true : undefined}>
                <SupportWidget />
                <AutoErrorToast />
            </div>
        </div>
        </LayoutWorkspaceContext.Provider>
    );
}
